<template>
  <div class="widget-picker-panel flex flex-col gap-4">
    <div>
      <p class="mb-2 text-sm font-medium text-text-secondary">{{ $t('W: Valgte widgets') }}</p>

      <UiEditableList
        :items="modelValue"
        isDraggable
        isRemovable
        :emptyStateText="$t('W: Ingen widgets tilføjet endnu')"
        @update:items="(items: unknown[]) => $emit('update:modelValue', items as IEditableWidgetInstance[])"
        @on-remove="(item: unknown) => removeWidget(asWidget(item).id)">
        <template #item="{ item }">
          <div class="flex min-w-0 flex-1 items-center gap-3">
            <component
              :is="getWidgetTypeIcon(asWidget(item).type)"
              class="h-5 w-5 shrink-0 text-text-secondary" />
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="truncate text-sm text-text-primary">
                {{ getWidgetTypeLabel(asWidget(item).type) }}
              </span>
              <span class="truncate text-xs text-text-secondary">
                {{ getWidgetTypeDescription(asWidget(item).type) }}
              </span>
            </div>
            <MenuTriggerWrap
              v-if="hasSettings(asWidget(item).type)"
              menuName="WidgetSettingsMenu"
              width="dynamic"
              :menuProps="getWidgetSettingsMenuProps(asWidget(item))">
              <IconButton v-tooltip="$t('W: Indstillinger')">
                <IconLucideChevronDown class="h-4 w-4" />
              </IconButton>
            </MenuTriggerWrap>
            <div class="w-32 shrink-0">
              <UiInputSelectVee
                :name="'size-' + asWidget(item).id"
                :options="sizeOptions"
                :modelValue="asWidget(item).size"
                @update:model-value="(size: TWidgetSize) => updateWidget(asWidget(item).id, { size })" />
            </div>
          </div>
        </template>
      </UiEditableList>
    </div>

    <div class="border-t-2 border-dashed border-border-subtle pt-4">
      <p class="mb-2 text-sm font-medium text-text-secondary">{{ $t('W: Tilføj widget') }}</p>

      <UiEditableList
        :items="availableCatalogEntries"
        isCatalog
        itemKey="type"
        :emptyStateText="$t('W: Alle tilgængelige widgets er tilføjet')"
        @on-catalog-select="(item: unknown) => addWidget(asCatalogEntry(item).type)">
        <template #item="{ item }">
          <div class="flex min-w-0 flex-1 items-center gap-3">
            <component
              :is="getWidgetTypeIcon(asCatalogEntry(item).type)"
              class="h-5 w-5 shrink-0 text-text-secondary" />
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="truncate text-sm text-text-primary">{{ getWidgetTypeLabel(asCatalogEntry(item).type) }}</span>
              <span class="truncate text-xs text-text-secondary">
                {{ getWidgetTypeDescription(asCatalogEntry(item).type) }}
              </span>
            </div>
          </div>
        </template>
      </UiEditableList>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import {
  getWidgetCatalogEntry,
  getWidgetCatalogEntries,
} from '@team-milva/lab-models/widget'
import type {
  IWidgetCatalogEntry,
  TWidgetMetric,
  TWidgetPeriod,
  TWidgetSize,
  TWidgetSurface,
  TWidgetType,
} from '@team-milva/lab-models/widget'
import {
  getWidgetTypeLabel,
  getWidgetTypeDescription,
  getWidgetTypeIcon,
} from '@/globals/typescript/utils/widgetLabels'
import { getOrderPriorityTierRangeLabel } from '@/globals/typescript/utils/timeRemainingChip'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { useUserStore } from '@/stores/userStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'

const ITEM_LIMIT_TYPES: TWidgetType[] = [
  'my-recent-activity',
  'urgent-deliveries',
  'urgent-analyses',
  'top-customers',
]

const METRIC_AND_PERIOD_TYPES: TWidgetType[] = ['period-graph', 'top-customers']

const ANALYSIS_FILTER_TYPES: TWidgetType[] = ['urgent-analyses']

// This panel edits every widget type generically, so `config` is modelled here as the union
// of every field any widget type might have (all optional) — each specific widget component
// still gets the exact, non-optional config shape for its own type.
export interface IEditableWidgetInstance {
  id: string
  type: TWidgetType
  size: TWidgetSize
  config: {
    itemLimit?: number
    metric?: TWidgetMetric
    period?: TWidgetPeriod
    analysisIDs?: string[]
    tierIDs?: string[]
    locationIDs?: string[]
  }
}

export default {
  name: 'WidgetPickerPanel',

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Array as PropType<IEditableWidgetInstance[]>,
      required: true,
    },
    surface: {
      type: String as PropType<TWidgetSurface>,
      required: true,
    },
    excludedTypes: {
      type: Array as PropType<TWidgetType[]>,
      default: () => [],
    },
    // Dashboard filters the "add widget" list by the current user's permissions
    // (hide, don't disable). Displays are deliberately not permission-gated —
    // see Dashboard & Displays plan, Roles & Permissions.
    isPermissionFiltered: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useLabLocationStore, ['activeLabLocations']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useAppStore, ['currentLocale']),

    sizeOptions(): { value: string; text: string }[] {
      return [
        { value: 'one-third', text: this.$t('W: 1/3 bredde') },
        { value: 'half', text: this.$t('W: 1/2 bredde') },
        { value: 'full', text: this.$t('W: Fuld bredde') },
      ]
    },

    metricOptions(): { value: string; text: string }[] {
      return [
        { value: 'orders', text: this.$t('W: Ordrer') },
        { value: 'samples', text: this.$t('W: Prøver') },
        { value: 'revenue', text: this.$t('W: Omsætning') },
      ]
    },

    periodOptions(): { value: string; text: string }[] {
      return [
        { value: '7-days', text: this.$t('W: 7 dage') },
        { value: '1-month', text: this.$t('W: 1 måned') },
        { value: '3-months', text: this.$t('W: 3 måneder') },
      ]
    },

    isPriorityColoringEnabled(): boolean {
      return this.labAccount?.orderPriorityConfig?.enabled ?? false
    },

    configuredTiers() {
      return this.labAccount?.orderPriorityConfig?.tiers ?? []
    },

    tierOptions(): { value: string; text: string }[] {
      return this.configuredTiers.map((tier) => ({
        value: tier.id,
        text: getOrderPriorityTierRangeLabel(this.configuredTiers, tier),
      }))
    },

    analysisOptions(): { value: string; text: string }[] {
      return this.allAnalyses
        .filter((analysis) => analysis.activeState === 'active')
        .map((analysis) => ({
          value: analysis.id,
          text: analysis.getName(this.currentLocale),
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    locationOptions(): { value: string; text: string }[] {
      return this.activeLabLocations
        .map((labLocation) => ({
          value: labLocation.id,
          text: labLocation.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    availableCatalogEntries(): IWidgetCatalogEntry[] {
      const userStore = useUserStore()
      const selectedTypes = new Set(this.modelValue.map((widget) => widget.type))
      return getWidgetCatalogEntries().filter(
        (entry) =>
          entry.availableSurfaces.includes(this.surface) &&
          !selectedTypes.has(entry.type) &&
          !this.excludedTypes.includes(entry.type) &&
          (!this.isPermissionFiltered || userStore.canUser(entry.permission))
      )
    },
  },

  methods: {
    getWidgetTypeLabel,
    getWidgetTypeDescription,
    getWidgetTypeIcon,

    hasItemLimit(type: TWidgetType): boolean {
      return ITEM_LIMIT_TYPES.includes(type)
    },

    hasMetricAndPeriod(type: TWidgetType): boolean {
      return METRIC_AND_PERIOD_TYPES.includes(type)
    },

    hasAnalysisFilters(type: TWidgetType): boolean {
      return ANALYSIS_FILTER_TYPES.includes(type)
    },

    hasSettings(type: TWidgetType): boolean {
      return (
        this.hasItemLimit(type) || this.hasMetricAndPeriod(type) || this.hasAnalysisFilters(type)
      )
    },

    asWidget(item: unknown): IEditableWidgetInstance {
      return item as IEditableWidgetInstance
    },

    asCatalogEntry(item: unknown): IWidgetCatalogEntry {
      return item as IWidgetCatalogEntry
    },

    addWidget(type: TWidgetType): void {
      const entry = getWidgetCatalogEntry(type)
      if (!entry) {
        return
      }

      const newWidget: IEditableWidgetInstance = {
        id: generateFirestoreId(),
        type,
        size: entry.defaultSize,
        config: { ...entry.defaultConfig },
      }

      this.$emit('update:modelValue', [...this.modelValue, newWidget])
    },

    removeWidget(widgetID: string): void {
      this.$emit(
        'update:modelValue',
        this.modelValue.filter((widget) => widget.id !== widgetID)
      )
    },

    updateWidget(widgetID: string, patch: Partial<IEditableWidgetInstance>): void {
      this.$emit(
        'update:modelValue',
        this.modelValue.map((widget) => (widget.id === widgetID ? { ...widget, ...patch } : widget))
      )
    },

    updateWidgetConfig(
      widgetID: string,
      configPatch: Partial<IEditableWidgetInstance['config']>
    ): void {
      this.$emit(
        'update:modelValue',
        this.modelValue.map((widget) =>
          widget.id === widgetID
            ? { ...widget, config: { ...widget.config, ...configPatch } }
            : widget
        )
      )
    },

    // Opens as a proper teleported/viewport-positioned menu (GlobalMenuHost)
    // instead of an inline expand-in-place panel — the analysis-filter
    // comboboxes can't escape this panel's own scrollable ancestor when
    // rendered inline, so they get clipped when the widget row sits near
    // the bottom of the modal. Every widget with any settings uses this
    // same menu (with only the relevant sections rendered) rather than
    // splitting settings across an inline panel and a separate menu. See
    // Dashboard & Displays plan, Phase 17 notes.
    getWidgetSettingsMenuProps(widget: IEditableWidgetInstance): Record<string, unknown> {
      return {
        hasItemLimit: this.hasItemLimit(widget.type),
        hasMetricAndPeriod: this.hasMetricAndPeriod(widget.type),
        hasAnalysisFilters: this.hasAnalysisFilters(widget.type),
        isPeriodGraph: widget.type === 'period-graph',
        itemLimit: widget.config.itemLimit,
        metric: widget.config.metric,
        period: widget.config.period,
        metricOptions: this.metricOptions,
        periodOptions: this.periodOptions,
        analysisIDs: widget.config.analysisIDs ?? [],
        tierIDs: widget.config.tierIDs ?? [],
        locationIDs: widget.config.locationIDs ?? [],
        analysisOptions: this.analysisOptions,
        tierOptions: this.tierOptions,
        locationOptions: this.locationOptions,
        isPriorityColoringEnabled: this.isPriorityColoringEnabled,
        onUpdate: (patch: Partial<IEditableWidgetInstance['config']>) =>
          this.updateWidgetConfig(widget.id, patch),
      }
    },
  },
}
</script>

<style scoped>
/* BaseFormFieldVee's root (.s-vee-form-field) always applies a 12px bottom
   margin (mb-3) — a fallthrough class here can't reliably reach past the
   nested <Field>/BaseFormFieldVee wrapper to cancel it, which left the size
   select sitting above-center relative to the plain expand/delete icons in
   the same row (see SettingsPriorityColorsView.vue's .tier-row for the same
   fix). Reaching in directly is the reliable fix — safe everywhere in this
   panel since the metric/period fields use `gap-*` for their own spacing,
   not this margin. */
.widget-picker-panel :deep(.s-vee-form-field) {
  margin-bottom: 0;
}
</style>
